import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Clock, TrendingUp } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { renderBlock } from "@/lib/richtext";

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/articles/${slug}`)
      .then((res) => setArticle(res.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div data-testid="article-not-found" className="flex min-h-screen flex-col items-center justify-center bg-[#FFFFFF] text-slate-900">
        <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-slate-500">Article not found</p>
        <Link to="/" className="mt-6 border border-[#6D28D9]/40 px-6 py-3 font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#6D28D9]">
          Back to the site
        </Link>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FFFFFF]">
        <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-slate-500">Loading note…</p>
      </div>
    );
  }

  return (
    <div data-testid="article-detail-page" className="min-h-screen bg-[#FFFFFF] text-slate-900">
      <header className="border-b border-slate-200 bg-[#FFFFFF]/80 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-10">
          <Link to="/" data-testid="article-back-button" className="flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.22em] text-slate-500 transition-colors hover:text-[#6D28D9]">
            <ArrowLeft className="h-4 w-4" /> Insights
          </Link>
          <span className="flex items-center gap-3 font-display text-lg font-semibold tracking-[0.18em]">
            <img src="/logo.png" alt="PRE-IPO EXPERT" className="h-10 w-10 rounded-full object-contain" />
            <span className="hidden whitespace-nowrap sm:inline">PRE-IPO <span className="text-[#6D28D9]">EXPERT</span></span>
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16 lg:py-24">
        <motion.article initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="flex flex-wrap items-center gap-3">
            <span className="border border-[#6D28D9]/30 bg-[#6D28D9]/[0.06] px-3 py-1 font-mono2 text-[9px] uppercase tracking-[0.2em] text-[#6D28D9]">
              {article.category}
            </span>
            <span className="flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-500">
              <Clock className="h-3 w-3" /> {article.read_time}
            </span>
            <span className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-500">{article.date}</span>
          </div>

          <h1 data-testid="article-title" className="font-display mt-6 text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            {article.title}
          </h1>
          <p className="mt-6 border-l-2 border-[#6D28D9] pl-5 text-base italic leading-relaxed text-slate-500 sm:text-lg">
            {article.excerpt}
          </p>

          {article.cover_image && (
            <div className="mt-10 overflow-hidden border border-slate-200">
              <img data-testid="article-cover-image" src={fileUrl(article.cover_image)} alt={article.title} className="max-h-[460px] w-full object-cover" />
            </div>
          )}

          <div data-testid="article-body" className="mt-12 space-y-7">
            {(article.body || []).map((para, i) => renderBlock(para, i))}
          </div>

          <div className="mt-14 border border-[#6D28D9]/25 bg-[#6D28D9]/[0.05] p-8">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-5 w-5 text-[#6D28D9]" />
              <h3 className="font-display text-xl font-semibold tracking-tight">Put this insight to work</h3>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate-500">
              Browse the current unlisted opportunities board or ask the desk a question — no obligation.
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <Link
                data-testid="article-explore-button"
                to="/"
                className="group flex items-center gap-3 bg-[#6D28D9] px-7 py-3.5 font-mono2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#FFFFFF] transition-transform duration-300 hover:scale-[1.03]"
              >
                Explore Opportunities <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
              </Link>
              <a
                data-testid="article-whatsapp-button"
                href="https://wa.me/918169647280?text=Hi%20PRE-IPO%20EXPERT%2C%20I%20read%20your%20insight%20note%20and%20have%20a%20question."
                target="_blank"
                rel="noopener noreferrer"
                className="border border-emerald-600/40 bg-emerald-600/[0.08] px-7 py-3.5 font-mono2 text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-700 transition-colors duration-300 hover:bg-emerald-600/20"
              >
                Ask on WhatsApp
              </a>
            </div>
          </div>

          <p className="mt-10 font-mono2 text-[10px] uppercase leading-relaxed tracking-[0.15em] text-slate-400">
            Information and education only — not investment advice. Unlisted securities carry significant risk, including possible loss of capital.
          </p>
        </motion.article>
      </main>
    </div>
  );
}
